// src/lib/template-variables.js — Phase 2.1
// Résolveur de variables {placeholder} dans les templates messages voyageurs.
//
// listAvailableVariables() : retourne la liste catégorisée pour l'UI
// (pickup dropdown dans MessagesPanel).
//
// renderTemplate(body, ctx) : remplace {variable_name} par la valeur
// résolue depuis ctx = { reservation, logement, cfg, language }.
// Variables manquantes → préservées textuellement (utile au debug, mieux
// qu'un envoi avec "undefined").
//
// Format date : Intl.DateTimeFormat selon ctx.language (fallback 'fr').
//
// Usage : import { listAvailableVariables, renderTemplate } from "../lib/template-variables"
//
// NOTE Phase 2.3 : pour l'envoi serveur (pas juste le preview client),
// dupliquer ce module en CommonJS dans /lib/template-variables.js. Ce
// fichier ESM reste réservé à l'UI.

// Chantier moteur méthode d'envoi (M5, ADR-267) : dérivation caution
// source-aware pour la variable inverse {caution_manquante} (miroir CJS :
// lib/payment-derivation.js — parité testée).
import { cautionRecue } from "./payment-derivation";
// ADR-174 (CHEMIN ARGENT — reflet) : mode de caution empreinte/débit dérivé de
// la SOURCE UNIQUE lib/caution-mode.js — le MÊME module (seuil
// CAUTION_HOLD_MAX_NIGHTS) qui pilote la décision Stripe dans api/stripe.js. Les
// variables {caution_empreinte}/{caution_debit} ne font que REFLÉTER cette
// décision (elles ne la pilotent jamais) → le wording dit TOUJOURS la vérité.
// Depuis F10 phase 2 (03/10), src/lib/caution-mode.js EST la source unique
// (CommonJS) ; lib/caution-mode.js n'est qu'un relais vers lui. Plus aucune
// copie de la règle : serveur et écran lisent le même fichier.
// Matrice « parcours voyageur » : le MÊME code que lib/template-variables.js
// (source unique partagée avec le serveur, F10 phase 2).
import { matrixCautionOn } from "./journey-composition-resolver";
// Décision 31 (29/10) : logement à 0 € voulu = pas de caution, toutes plateformes.
import { cautionPourSejour, lireFigee } from "./caution-figee";
// « le 1er août », jamais « le 1 août » — SOURCE UNIQUE de la règle française
// du premier du mois, partagée avec les ÉCRANS (miroir CJS :
// lib/date-fr-ordinal.js — parité testée). Cf. le commentaire de `fmtDate`.
import { frPremierDuMois } from "./date-fr-ordinal";

// ── Catalogue des variables disponibles (UI dropdown) ──────────────

export function listAvailableVariables() {
  return [
    {
      // Lot H — insertion de blocs conditionnels {#if key}…{/if} (le contenu du
      // bloc n'apparaît que si la variable vaut "true"). `insertAs:'block'` dit à
      // MessageEditor.insertVariable d'insérer les balises encadrantes et de
      // placer le curseur DEDANS, au lieu d'un simple {key}. Ces trois clés
      // existent aussi (plates) dans « Computed » pour un usage inline.
      category: "🧩 Bloc conditionnel",
      variables: [
        {
          key: "is_airbnb",
          label: "Bloc affiché seulement si la réservation vient d'Airbnb",
          insertAs: "block",
        },
        {
          key: "is_booking",
          label: "Bloc affiché seulement si la réservation vient de Booking",
          insertAs: "block",
        },
        {
          key: "is_direct",
          label: "Bloc affiché seulement si la réservation est en direct",
          insertAs: "block",
        },
        // Chantier moteur méthode d'envoi (M5, ADR-267) — variables INVERSES :
        // non-vides quand la chose MANQUE → le bloc {#if …} s'affiche
        // seulement si le voyageur n'est PAS en règle. Zéro modification du
        // parseur {#if} (IF_BLOCK_RE).
        {
          key: "caution_manquante",
          label: "Bloc affiché seulement si la caution n'est PAS reçue",
          insertAs: "block",
        },
        // ADR-174 — MODE de caution (reflet EXACT de la décision Stripe) : selon
        // la durée du séjour, la caution est prise en empreinte (jamais débitée)
        // ou en débit réel. Blocs exclusifs (exactement un actif à la fois).
        {
          key: "caution_empreinte",
          label:
            "Bloc affiché seulement si la caution est une empreinte (jamais débitée)",
          insertAs: "block",
        },
        {
          key: "caution_debit",
          label:
            "Bloc affiché seulement si la caution est un débit réel (prélevé puis remboursé)",
          insertAs: "block",
        },
        {
          key: "formulaire_manquant",
          label:
            "Bloc affiché seulement si le formulaire de bienvenue n'est PAS rempli",
          insertAs: "block",
        },
      ],
    },
    {
      category: "👤 Voyageur",
      variables: [
        { key: "guest_first_name", label: "Prénom", example: "Sophie" },
        { key: "guest_last_name", label: "Nom de famille", example: "Martin" },
        {
          key: "guest_email",
          label: "Email",
          example: "sophie.martin@example.fr",
        },
        {
          key: "guest_phone",
          label: "Téléphone",
          example: "+33 6 12 34 56 78",
        },
        { key: "guest_language", label: "Langue", example: "fr" },
        { key: "guest_count", label: "Nombre de personnes", example: "2" },
      ],
    },
    {
      category: "📅 Réservation",
      variables: [
        {
          key: "checkin_date",
          label: "Date d'arrivée (formatée)",
          example: "5 mai 2026",
        },
        {
          key: "checkout_date",
          label: "Date de départ (formatée)",
          example: "8 mai 2026",
        },
        { key: "nights", label: "Nombre de nuits", example: "3" },
        { key: "ota_source", label: "Plateforme", example: "Airbnb" },
        { key: "total_amount", label: "Montant total (€)", example: "184,00" },
        { key: "cleaning_fee", label: "Frais ménage (€)", example: "40,00" },
        {
          key: "ota_chat_link",
          label: "Lien messagerie OTA",
          example: "https://airbnb.com/...",
        },
        // PARTIE B (ADR-272) — variables Direct (fail-doux '' côté Plateformes).
        {
          key: "request_ref",
          label: "Référence de la demande",
          example: "a1b2c3d4",
        },
        {
          key: "cancellation_policy",
          label: "Politique d'annulation",
          example: "Annulation gratuite jusqu'à 7 jours avant l'arrivée.",
        },
      ],
    },
    {
      category: "🏠 Logement",
      variables: [
        {
          key: "logement_name",
          label: "Nom du logement (court)",
          example: "Cocon Naturel",
        },
        {
          key: "logement_full_name",
          label: "Nom complet (fallback nom court si vide)",
          example: "Cocon Naturel — 12 rue d'Alsace, Toulouse",
        },
        {
          key: "logement_address",
          label: "Adresse de base",
          example: "12 rue d'Alsace, Toulouse",
        },
        {
          key: "logement_address_full",
          label: "Adresse complète (Phase 2.A)",
          example: "12 rue d'Alsace, 31000 Toulouse",
        },
        // ADR-272 — {ville} : source dédiée public_address.ville (fallback l.city).
        { key: "ville", label: "Ville du logement", example: "Toulouse" },
        {
          key: "logement_address_precision",
          label: "Précisions (étage, bât., porte) (Phase 2.A)",
          example: "Bâtiment A, RDC droite",
        },
        { key: "logement_floor", label: "Étage", example: "2e étage" },
        {
          key: "access_building_instructions",
          label: "Instructions accès bâtiment (Phase 2.A)",
          example: "Sonnez à l'interphone FULLBNBZEN…",
        },
        {
          key: "access_apartment_instructions",
          label: "Instructions accès appartement (Phase 2.A)",
          example: "2e étage, porte de droite. Boîte à clés…",
        },
        { key: "wifi_name", label: "Wi-Fi (SSID)", example: "Bbox-AB12CD" },
        {
          key: "wifi_code",
          label: "Wi-Fi (mot de passe)",
          example: "M3rcr3di!",
        },
        {
          key: "wifi_instructions",
          label: "Instructions Wi-Fi détaillées (Phase 2.A)",
          example: "Connectez-vous au réseau…",
        },
        { key: "door_code", label: "Code porte d'entrée", example: "4521A" },
        { key: "box_code", label: "Code boîte à clés", example: "1234" },
        { key: "interphone_code", label: "Code interphone", example: "23B17" },
        { key: "coffre_code", label: "Code coffre", example: "0000" },
        { key: "garage_code", label: "Code garage", example: "9876" },
        {
          key: "parking_info",
          label: "Parking (instructions courtes)",
          example: "Parking gratuit rue X",
        },
        {
          key: "parking_info_long",
          label: "Parking (instructions longues) (Phase 2.A)",
          example: "Parking public gratuit rue d'Alsace…",
        },
        {
          key: "poubelles_info",
          label: "Poubelles (instructions courtes)",
          example: "Containers verts au coin",
        },
        {
          key: "infos_poubelles_long",
          label: "Poubelles (instructions longues) (Phase 2.A)",
          example: "Containers gris ordures au coin…",
        },
        {
          key: "infos_courses_stockage",
          label: "Courses & stockage (Phase 2.A)",
          example: "Carrefour City à 100m. Réfrigérateur…",
        },
        {
          key: "regles_maison",
          label: "Règles de la maison (Phase 2.A)",
          example: "Non-fumeur · Pas de fêtes…",
        },
        {
          key: "livret_url",
          label: "URL livret d'accueil (Phase 2.A)",
          example: "https://livret.fullbnbzen.com/…",
        },
        { key: "checkin_time", label: "Heure de check-in", example: "16:00" },
        { key: "checkout_time", label: "Heure de check-out", example: "10:00" },
        // ADR-272 — ALIAS horaires (message d'arrivée) : même valeur que
        // checkin_time / checkout_time.
        { key: "heure_arrivee", label: "Heure d'arrivée", example: "16:00" },
        { key: "heure_depart", label: "Heure de départ", example: "10:00" },
        // PARTIE B (ADR-272) — lien avis Google du logement (config KV
        // self-only, comme les codes d'accès). Vide par défaut en prod.
        {
          key: "avis_google_url",
          label: "Lien avis Google",
          example: "https://g.page/r/EXEMPLE/review",
        },
      ],
    },
    {
      category: "🛎️ Conciergerie",
      variables: [
        { key: "fbz_name", label: "Nom société", example: "FULLBNBZEN" },
        { key: "fbz_phone", label: "Téléphone", example: "07 83 31 85 06" },
        // ADR-272 — ALIAS téléphone conciergerie (message d'arrivée) : même
        // valeur que fbz_phone.
        {
          key: "telephone_contact",
          label: "Téléphone (contact)",
          example: "07 83 31 85 06",
        },
        { key: "fbz_email", label: "Email", example: "contact@fullbnbzen.com" },
        {
          key: "fbz_logo_url",
          label: "URL logo",
          example: "https://sejour.fullbnbzen.com/logo.png",
        },
      ],
    },
    {
      // POINT 2 (conversion composites) — les booléens de condition portent
      // `insertAs:'block'` : insérés inline (hors {#if}) ils rendent "true"
      // dans le message. Le picker du SegmentEditor filtre insertAs:'block' →
      // ils ne sont plus proposés en insertion inline ; l'insertion en bloc
      // passe par le menu « Paragraphe par plateforme » (et POINT 4 à venir).
      category: "🧮 Computed (Phase 2.B)",
      variables: [
        {
          key: "is_airbnb",
          label: '"true" si source=airbnb (utiliser dans {#if is_airbnb})',
          example: "true",
          insertAs: "block",
        },
        {
          key: "is_booking",
          label: '"true" si source=booking',
          example: "true",
          insertAs: "block",
        },
        {
          key: "is_direct",
          label: '"true" si source=direct',
          example: "true",
          insertAs: "block",
        },
        {
          key: "caution_required",
          label: '"true" si Booking/Direct (caution Stripe à demander)',
          example: "true",
          insertAs: "block",
        },
        // POINT 2 — {rating_phrase} retirée du catalogue (Option A) : la
        // composite opaque est remplacée par des encarts {#if is_airbnb}/
        // {#if is_booking} explicites dans relance_avis. Le RÉSOLVEUR garde
        // son case (DEPRECATED) pour la rétrocompat brouillons/historique.
        {
          key: "source",
          label: "Source brute (airbnb/booking/direct)",
          example: "airbnb",
        },
        {
          key: "revenue",
          label: "Alias total_amount formaté",
          example: "184,00",
        },
        {
          key: "date_arrivee",
          label: "Alias checkin_date (date d'arrivée formatée)",
          example: "5 mai 2026",
        },
        {
          key: "date_depart",
          label: "Alias checkout_date (date de départ formatée)",
          example: "8 mai 2026",
        },
        {
          key: "welcome_form_url",
          label:
            "URL formulaire de bienvenue (/bienvenue/<token>, générée par le moteur)",
          example: "https://sejour.fullbnbzen.com/bienvenue/...",
        },
        // Lot O1 OTA — lien unique espace voyageur OTA (généré JIT à l'envoi).
        {
          key: "lien_sejour",
          label:
            "Lien espace voyageur OTA (/sejour-ota/<token>, généré par le moteur)",
          example: "https://sejour.fullbnbzen.com/sejour-ota/...",
        },
        {
          key: "checkin_form_url",
          label: "URL formulaire check-in (Phase 3 — vide)",
          example: "https://forms.fbz/checkin/...",
        },
        {
          key: "deposit_url",
          label: "URL caution Stripe (Phase 5 — vide)",
          example: "https://buy.stripe.com/...",
        },
        {
          key: "payment_received",
          label: '"true" si payment_received_at non null (Phase 2.B+)',
          example: "true",
          insertAs: "block",
        },
        {
          key: "caution_received",
          label: '"true" si caution_received_at non null (Phase 2.B+)',
          example: "true",
          insertAs: "block",
        },
        {
          key: "deposit_link_visible",
          label:
            '"true" si Booking/Direct ET payé (utiliser dans {#if deposit_link_visible})',
          example: "true",
          insertAs: "block",
        },
        {
          key: "welcome_checkin_allowed",
          label: '"true" si Airbnb OU caution reçue',
          example: "true",
          insertAs: "block",
        },
        // Phase 2 Stripe (ADR-108)
        // POINT 2 — {caution_status} et {paiement_sejour_status} retirées du
        // catalogue (picker-only, 0 usage prod) : statuts techniques Stripe
        // anglais, illisibles dans un message voyageur. Résolveurs intacts
        // (rétrocompat brouillons/historique).
        {
          key: "caution_amount",
          label: "Montant caution formaté EUR",
          example: "300,00",
        },
        {
          key: "caution_link",
          label: "URL paiement caution Stripe (Phase 5 direct booking)",
          example: "https://buy.stripe.com/...",
        },
        {
          key: "paiement_caution_url",
          label:
            "URL paiement caution Stripe automatique (Sprint 2.4 PR-B1 — trigger temporal_checkin_caution J-2)",
          example: "https://book.fullbnbzen.com/fr/paiement/<token>",
        },
        {
          key: "paiement_sejour_link",
          label: "URL paiement séjour Stripe",
          example: "https://buy.stripe.com/...",
        },
        {
          key: "caution_authorized",
          label: '"true" si caution_status=authorized (hold OK)',
          example: "true",
          insertAs: "block",
        },
        {
          key: "caution_captured",
          label: '"true" si caution_status=captured (sinistre)',
          example: "true",
          insertAs: "block",
        },
        {
          key: "paid_at_formatted",
          label: "Date paiement séjour formatée",
          example: "5 mai 2026",
        },
      ],
    },
  ];
}

// ── Default fallbacks pour le contact conciergerie ─────────────────

const FBZ_DEFAULT_PHONE = "07 83 31 85 06";
const FBZ_DEFAULT_EMAIL = "contact@fullbnbzen.com";
const FBZ_DEFAULT_NAME = "FULLBNBZEN";

// ── Whitelist Livret — champs anciennement hérités parent l8 → chambres ──
//
// ADR-197 (LOT 1 — coupure des fallbacks de CONTENU parent→chambre) : le SEED
// (LOT 0) a donné aux chambres lc1/lc2/lc3 leurs valeurs PROPRES (adresse,
// accès, parking, poubelles, courses, règles, codes, wifi, horaires…). Le
// fallback parent de CONTENU est donc retiré : `effectiveLogValue` lit
// désormais uniquement la valeur self du log.
//
// Cette Set est CONSERVÉE (exportée) à titre documentaire — elle liste les
// champs Livret qui étaient autrefois hérités. Elle n'est plus utilisée pour
// déclencher un fallback parent.
//
// ⚠️ ARGENT INTACT : les fallbacks parent du CHEMIN ARGENT (caution/Stripe
// dans cfg-reader-server.js, messages-engine.js, api/sites.js) restent en
// place — ils ne passent PAS par cette whitelist.
//
// ⚠️ DUPLICATION CONTRÔLÉE avec lib/template-variables.js (CJS serveur).
// Toute modif doit être appliquée aux 2 fichiers (ADR-021).
export const LIVRET_INHERITED_FIELDS = new Set([
  "adresse_complete", // strict
  "adresse_precision", // override possible
  "instructions_acces_batiment", // strict
  "instructions_acces_appartement", // override possible (Q1 = Cas 1 — porte commune appartement)
  "wifi_instructions", // override possible
  "parking_info_long", // strict
  "infos_poubelles_long", // strict
  "infos_courses_stockage", // strict
  "regles_maison", // override possible
  "livret_url", // override possible
  // PR #142A — codes accès structurés (override possible chambre)
  "door_code",
  "box_code",
  "interphone_code",
  "coffre_code",
  "garage_code",
]);

// Valeur effective d'un champ logement Livret : la valeur PROPRE du log
// (self), ou '' si vide/absente.
//
// ADR-197 (LOT 1) : le fallback parent de CONTENU a été retiré. Les chambres
// sous-loc lisent leurs valeurs seedées (LOT 0). Signature inchangée (compat
// appelants). Convention falsy : null/undefined/'' = "pas saisi" → ''.
export function effectiveLogValue(log, fieldName) {
  if (!log) return "";
  const own = log[fieldName];
  if (own != null && own !== "") return own;
  return "";
}

// ── resolveLogTime helper (PR #151 / ADR-086 ; ADR-197 coupure parent) ──
//
// Résout l'heure check-in ou check-out d'un logement (self) + parsing format
// libre "11h"/"11h30" → "HH:MM". Dupliqué symétriquement dans :
//   - src/lib/menage-status-helpers.js (ESM client menage)
//   - src/lib/template-variables.js     (ESM client preview — ce fichier)
//   - lib/template-variables.js         (CJS serveur)
// Toute modification doit être appliquée AUX TROIS fichiers (ADR-021).
//
// ADR-197 (LOT 1) : le fallback parent (chambres sous-loc) a été retiré —
// les chambres ont leur propre schedule checkin/checkout seedé (LOT 0). Le
// paramètre `parentLog` est conservé (compat appelants) mais n'est plus lu.
//
// LOT E (Chantier 12, décision Dimitri) — FLIP « Instructions-first » : la
// vérité horaire est le champ Instructions (`log.checkin_time`/`checkout_time`,
// ADR-199), pas `schedule.*`. On lit DONC le champ libre EN PREMIER, puis
// `schedule.checkin_start`/`checkout_time` en fallback. lc1/lc2/lc3 → 16h→17h ;
// checkout concordant (no-op) ; sans champ → fallback schedule (sûr).
function parseHourString(s) {
  if (!s || typeof s !== "string") return null;
  const trimmed = s.trim().toLowerCase();
  const m = trimmed.match(/^(\d{1,2})[h:.]?(\d{0,2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
}

/**
 * @param {Object|null} log
 * @param {Object|null} _parentLog - conservé pour compat, non utilisé (ADR-197)
 * @param {'checkin'|'checkout'} kind
 * @returns {string|null}
 */
export function resolveLogTime(log, _parentLog, kind) {
  if (kind !== "checkin" && kind !== "checkout") return null;
  const scheduleKey = kind === "checkin" ? "checkin_start" : "checkout_time";
  const kindField = kind === "checkin" ? "checkin_time" : "checkout_time";
  const tryOne = (l) => {
    if (!l) return null;
    // LOT E — field-first (Instructions = vérité, ADR-199), schedule en fallback.
    const parsedField = parseHourString(l[kindField]);
    if (parsedField) return parsedField;
    const sched = l.schedule && l.schedule[scheduleKey];
    const parsedSched = parseHourString(sched);
    if (parsedSched) return parsedSched;
    return null;
  };
  return tryOne(log);
}

// ── Helpers de formatage ───────────────────────────────────────────

// ⚠️ MIROIR EXACT de `fmtDate` (lib/template-variables.js, CJS serveur) — c'est
// LUI qui rend les emails/SMS réellement envoyés ; celui-ci sert les APERÇUS
// admin. Les deux doivent dire la même chose, sinon l'aperçu ment.
// Parité verrouillée par src/lib/template-variables-date-1er.test.js.
export function fmtDate(isoDate, language = "fr") {
  if (!isoDate) return "";
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return String(isoDate);
    const localeMap = { fr: "fr-FR", en: "en-US", es: "es-ES" };
    const locale = localeMap[language] || "fr-FR";
    // « 1er août » en français (et SEULEMENT en français, et SEULEMENT le 1er) —
    // SOURCE UNIQUE src/lib/date-fr-ordinal.js, la même règle que les écrans.
    return frPremierDuMois(
      new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(d),
      d,
      locale,
    );
  } catch (_) {
    return String(isoDate);
  }
}

function fmtMoney(n) {
  if (n == null || isNaN(Number(n))) return "";
  return Number(n).toFixed(2).replace(".", ",");
}

// ── BUG39 (source unique Wi-Fi) — dérivation {wifi_instructions} ──────
//
// DÉCISION Dimitri : la source unique Wi-Fi est la STRUCTURE wifi_networks
// (table livret_config, format [{ssid, password}]). Le texte libre
// wifi_instructions (KV cfg) reste un OVERRIDE prioritaire ; quand il est
// vide, on dérive AUTO le texte depuis wifi_networks.
//
// Format dérivé aligné sur le bouton « Migrer wifi » de LivretTab.jsx :
//   "Réseau {ssid} — Mot de passe : {password}"
// Multi-réseaux concaténés (une ligne par réseau). Un réseau sans password
// donne juste "Réseau {ssid}". Un réseau sans ssid est ignoré.
//
// ⚠️ DUPLICATION CONTRÔLÉE avec lib/template-variables.js (ADR-021).
// Toute modif doit être appliquée AUX DEUX fichiers.
// pickFirstWifi(arr) — premier réseau valide (objet) du tableau wifi_networks,
// ou null. Sert à résoudre {wifi_name} (ssid) et {wifi_code} (password) depuis
// la SOURCE UNIQUE livret_config.wifi_networks (décision Dimitri, Front 2).
//
// ⚠️ DUPLICATION CONTRÔLÉE avec lib/template-variables.js (ADR-021).
// Toute modif doit être appliquée AUX DEUX fichiers.
export function pickFirstWifi(arr) {
  if (!Array.isArray(arr)) return null;
  for (const net of arr) {
    if (net && typeof net === "object") return net;
  }
  return null;
}

export function deriveWifiInstructions(wifiNetworks) {
  if (!Array.isArray(wifiNetworks)) return "";
  const lines = [];
  for (const net of wifiNetworks) {
    if (!net || typeof net !== "object") continue;
    const ssid = typeof net.ssid === "string" ? net.ssid.trim() : "";
    if (!ssid) continue;
    const password =
      typeof net.password === "string" ? net.password.trim() : "";
    lines.push(
      password ? `Réseau ${ssid} — Mot de passe : ${password}` : `Réseau ${ssid}`,
    );
  }
  return lines.join("\n");
}

function nightsBetween(start, end) {
  if (!start || !end) return "";
  try {
    const s = new Date(start),
      e = new Date(end);
    const diffMs = e - s;
    if (isNaN(diffMs) || diffMs <= 0) return "";
    return String(Math.round(diffMs / (1000 * 60 * 60 * 24)));
  } catch (_) {
    return "";
  }
}

// ADR-174 — nombre de nuits pour le MODE de caution (empreinte/débit).
// Miroir de _cautionNbNuits (lib/template-variables.js) : réutilise
// nightsBetween (source canonique des nuits, alignée sur date_arrivee/date_depart)
// et le ramène à un entier ; dates absentes/invalides → "" → 0 (fail-safe :
// cautionModeForNights(0) = 'empreinte', mode le plus prudent).
function _cautionNbNuits(r) {
  const n = Number(nightsBetween(r.start_date, r.end_date));
  return Number.isFinite(n) ? n : 0;
}

// ── Resolver principal ─────────────────────────────────────────────

// Décision 31 (29/10) — logement réglé à 0 € VOULU (lecture logement → parent,
// source unique) : les variables EFFECTIVES ne réclament plus de caution, comme
// une matrice éteinte. Logement absent du contexte → false (comportement d'avant).
// Une réservation qui a DÉJÀ un montant de caution fixé (> 0) garde sa caution,
// même si le logement passe ensuite à 0 — même règle que l'envoi
// (cautionSansObjet, lib/messages/conditions.js).
function _cautionLogementAZero(ctx) {
  const r = (ctx && ctx.reservation) || {};
  // P-2.56 (30/10) : par LA porte — 0 figé = voulu ; montant figé ou posé > 0
  // garde sa caution ; sinon le réglage du logement POUR LA DURÉE (paliers).
  return _cautionSejour(ctx).zero;
}

// P-2.56 (30/10) — la caution du séjour par LA porte (src/lib/caution-figee.js) :
// figée sur la résa si elle la porte, sinon le réglage du logement pour la durée.
// Les mêmes montant et mode que la session Stripe.
function _cautionSejour(ctx) {
  const r = (ctx && ctx.reservation) || {};
  return cautionPourSejour({
    figee: r,
    log: (ctx && ctx.logement) || null,
    nuits: _cautionNbNuits(r),
  });
}

export function resolveValue(key, ctx) {
  const r = ctx.reservation || {};
  const l = ctx.logement || {};
  const c = ctx.cfg || {};
  const co = c.co || {};
  const language = ctx.language || r.guest_language || "fr";

  switch (key) {
    // Voyageur
    case "guest_first_name":
      return r.guest_first_name || "";
    case "guest_last_name":
      return r.guest_last_name || "";
    case "guest_email":
      return r.guest_email || "";
    case "guest_phone":
      return r.guest_phone || "";
    case "guest_language":
      return r.guest_language || "";
    case "guest_count":
      return r.guest_count != null ? String(r.guest_count) : "";

    // Réservation
    case "checkin_date":
      return fmtDate(r.start_date, language);
    case "checkout_date":
      return fmtDate(r.end_date, language);
    case "nights":
      return nightsBetween(r.start_date, r.end_date);
    case "ota_source":
      return r.source
        ? r.source.charAt(0).toUpperCase() + r.source.slice(1)
        : "";
    case "total_amount":
      return fmtMoney(r.total_amount);
    case "cleaning_fee":
      return fmtMoney(r.cleaning_fee);
    case "ota_chat_link":
      return r.ota_chat_link || "";
    // PARTIE B (ADR-272) — variables Direct ajoutées au catalogue partagé
    // (parité ADR-021). Résolveurs FAIL-DOUX : '' si le contexte ne porte pas
    // la valeur (ex. résa Plateformes). Côté parcours Direct, la valeur est
    // fournie par le builder (request_ref ≡ requestIdShort ;
    // cancellation_policy ≡ politiqueAnnulationText).
    case "request_ref":
      return r.request_ref || "";
    case "cancellation_policy":
      return r.cancellation_policy || "";

    // Logement (depuis cfg.props[].logs[])
    // ADR-197 (LOT 1) : les champs Livret lisent désormais la valeur self du
    // log (effectiveLogValue sans fallback parent — chambres seedées LOT 0).
    case "logement_name":
      return l.nom || "";
    case "logement_full_name":
      return l.nom_complet || l.nom || "";
    case "logement_address":
      return l.adresse || co.adresse || "";
    case "logement_address_full":
      return effectiveLogValue(l, "adresse_complete");
    case "logement_address_precision":
      return effectiveLogValue(l, "adresse_precision");
    // {ville} — VILLE du logement. Source structurée DÉDIÉE public_address.ville
    // (peuplée depuis Beds24 `city`, éditable onglet Website ; même résolution que
    // lib/inbox-ai-handlers.js `ctx.city`), fallback l.city. Fail-doux '' si aucune
    // source propre (champ à renseigner par logement) — PAS d'extraction regex sur
    // l'adresse complète.
    case "ville": {
      const pa = l.public_address;
      return (pa && typeof pa === "object" && pa.ville) || l.city || "";
    }
    case "logement_floor":
      return l.floor || "";
    case "access_building_instructions":
      return effectiveLogValue(l, "instructions_acces_batiment");
    case "access_apartment_instructions":
      return effectiveLogValue(l, "instructions_acces_appartement");
    // Front 2 (source unique Wi-Fi) : {wifi_name} = SSID, {wifi_code} = mot de
    // passe du 1er réseau de ctx.wifi_networks (livret_config, injecté top-level
    // par les callers). Fallback legacy défensif sur l.wifi_name / l.wifi_code.
    case "wifi_name":
      return pickFirstWifi(ctx.wifi_networks)?.ssid || l.wifi_name || "";
    case "wifi_code":
      return pickFirstWifi(ctx.wifi_networks)?.password || l.wifi_code || "";
    case "wifi_instructions": {
      // BUG39 : texte libre (override) prioritaire ; sinon dérivation auto
      // depuis la structure wifi_networks (livret_config), passée top-level
      // dans ctx (pattern miroir ctx.livret_url, plumbing messages-engine).
      const override = effectiveLogValue(l, "wifi_instructions");
      if (override) return override;
      return deriveWifiInstructions(ctx.wifi_networks);
    }
    case "door_code":
      return effectiveLogValue(l, "door_code");
    case "box_code":
      return effectiveLogValue(l, "box_code");
    case "interphone_code":
      return effectiveLogValue(l, "interphone_code");
    case "coffre_code":
      return effectiveLogValue(l, "coffre_code");
    case "garage_code":
      return effectiveLogValue(l, "garage_code");
    // PARTIE B (ADR-272) — lien avis Google du logement (config KV self-only,
    // même pattern que door_code/wifi/box_code). Vide par défaut en prod (à
    // renseigner par logement, cf. prérequis d'activation). '' si absent.
    case "avis_google_url":
      return effectiveLogValue(l, "avis_google_url");
    // 🔴 TOUJOURS VIDE — corrigé le 2026-08-05. `parking_info` (version
    // courte) n'existe sur AUCUN logement en base : l'admin ne saisit que la
    // version longue, dans Livret > Instructions. Les messages contenant
    // {parking_info} partaient donc systématiquement avec un trou, alors que
    // l'information EXISTE (7 logements sur 12 la renseignent).
    // Repli sur la version longue : « une info correcte plutôt qu'un vide ».
    case "parking_info":
      return l.parking_info || effectiveLogValue(l, "parking_info_long");
    case "parking_info_long":
      return effectiveLogValue(l, "parking_info_long");
    // 🔴 Même défaut que parking_info : `poubelles_info` n'existe nulle part,
    // seule `infos_poubelles_long` est saisie (4 logements sur 12).
    case "poubelles_info":
      return l.poubelles_info || effectiveLogValue(l, "infos_poubelles_long");
    case "infos_poubelles_long":
      return effectiveLogValue(l, "infos_poubelles_long");
    case "infos_courses_stockage":
      return effectiveLogValue(l, "infos_courses_stockage");
    case "regles_maison":
      return effectiveLogValue(l, "regles_maison");
    // Phase A.2 PR #190 (ADR-109) — priorité ctx.livret_url (token DB pré-résolu
    // par messages-engine.js anti-N+1) > effectiveLogValue (KV V1, fallback rétrocompat).
    case "livret_url":
      return ctx.livret_url || effectiveLogValue(l, "livret_url") || "";
    case "checkin_time":
      return resolveLogTime(l, null, "checkin") || "16:00";
    case "checkout_time":
      return resolveLogTime(l, null, "checkout") || "10:00";
    // ADR-272 (message d'instructions d'arrivée) — ALIAS horaires : MÊME valeur
    // que checkin_time/checkout_time (cibles inchangées), défauts 16:00 / 10:00.
    case "heure_arrivee":
      return resolveLogTime(l, null, "checkin") || "16:00";
    case "heure_depart":
      return resolveLogTime(l, null, "checkout") || "10:00";

    // Conciergerie (override par logement, fallback cfg.co, fallback hardcodé)
    case "fbz_name":
      return co.nom || FBZ_DEFAULT_NAME;
    case "fbz_phone":
      return l.contact_phone || co.tel || FBZ_DEFAULT_PHONE;
    // ADR-272 (message d'instructions d'arrivée) — ALIAS du téléphone conciergerie :
    // MÊME valeur que fbz_phone (cible inchangée).
    case "telephone_contact":
      return l.contact_phone || co.tel || FBZ_DEFAULT_PHONE;
    case "fbz_email":
      return l.contact_email || co.email || FBZ_DEFAULT_EMAIL;
    case "fbz_logo_url":
      // Lot 2.1 liens propres : côté client (aperçus admin), l'origin du
      // navigateur suit déjà le domaine servi ; fallback canonique sinon
      // (PR-A : plus jamais vercel.app).
      return (
        co.logoUrl ||
        `${(typeof window !== "undefined" && window.location && window.location.origin) || "https://sejour.fullbnbzen.com"}/logo.png`
      );

    // Computed Phase 2.B (booléens-string + alias + URLs Phases ult.)
    case "is_airbnb":
      return r.source === "airbnb" ? "true" : "";
    case "is_booking":
      return r.source === "booking" ? "true" : "";
    case "is_direct":
      return r.source === "direct" ? "true" : "";
    case "caution_required":
      return r.source === "booking" || r.source === "direct" ? "true" : "";
    // DEPRECATED (POINT 2 — conversion composites) : {rating_phrase} est
    // retirée du PICKER (catalogue ci-dessus) — remplacée dans relance_avis
    // par des encarts {#if is_airbnb}/{#if is_booking} explicites. Le case
    // est CONSERVÉ pour la rétrocompat : un brouillon/historique contenant
    // encore {rating_phrase} doit rester résoluble. Texte FR hardcodé connu
    // (bug i18n hérité — assumé sur le legacy). Ne pas réintroduire au picker.
    case "rating_phrase":
      if (r.source === "airbnb") return "une note de 5 étoiles";
      if (r.source === "booking") return "une note de 10/10";
      return ""; // direct = pas de relance avis
    case "source":
      return r.source || "";
    case "revenue":
      return fmtMoney(r.total_amount);
    case "date_arrivee":
      return fmtDate(r.start_date, language);
    case "date_depart":
      return fmtDate(r.end_date, language);
    // Chantier moteur méthode d'envoi (ADR-267) — REBRANCHÉE : priorité
    // ctx.welcome_form_url (injecté serveur par messages-engine, pattern
    // miroir paiement_caution_url). En preview admin, valeur vide → affichage
    // "{welcome_form_url}" textuel. Fallback legacy r.welcome_form_url.
    case "welcome_form_url":
      return ctx.welcome_form_url || r.welcome_form_url || "";
    // Lot O1 OTA — {lien_sejour} : URL de l'espace voyageur OTA
    // (/sejour-ota/<token>, token 'sejour_ota' généré JIT par messages-engine,
    // pattern EXACT welcome_form_url). En preview admin, valeur vide →
    // affichage "{lien_sejour}" textuel (la valeur DOIT venir du ctx moteur).
    case "lien_sejour":
      return ctx.lien_sejour || "";
    // LOT 3 — {code_promo} : code de FIDÉLITÉ -10% offert au voyageur pour un
    // futur séjour direct. MIROIR EXACT de lib/template-variables.js (parité
    // ADR-021) : lecture PURE du ctx, la valeur est résolue JIT à l'envoi par
    // lib/messages-engine.js (lib/promo-codes.js::getOrRefreshPromoCode). En
    // preview admin le ctx est décoré par
    // src/tabs/messages/preview-link-placeholders.js (placeholder honnête
    // « [code généré à l'envoi] ») — jamais un vide silencieux.
    case "code_promo":
      return ctx.code_promo || "";
    case "checkin_form_url":
      return r.checkin_form_url || ""; // Phase 3 (formulaire pré-arrivée)
    case "deposit_url":
      return r.deposit_url || ""; // Phase 5 (Stripe caution)

    // Phase 2.B+ (ADR-027) : statut paiement & caution sur la résa
    case "payment_received":
      return r.payment_received_at ? "true" : "";
    case "caution_received":
      return r.caution_received_at ? "true" : "";
    case "deposit_link_visible":
      return r.payment_received_at &&
        (r.source === "booking" || r.source === "direct")
        ? "true"
        : "";
    case "welcome_checkin_allowed":
      return r.source === "airbnb" || r.caution_received_at ? "true" : "";

    // Phase 2 Stripe (ADR-108) : variables enrichies caution + paiement séjour
    // NOTE (POINT 2) : {caution_status} et {paiement_sejour_status} sont
    // retirées du PICKER (statuts techniques Stripe, 0 usage prod) mais leurs
    // cases restent résolubles (rétrocompat brouillons/historique).
    case 'caution_status':         return r.caution_status || ''
    case 'caution_amount': {
      // P-2.56 : montant FIGÉ quand la résa le porte (0 = pas de caution).
      const figee = lireFigee(r)
      return fmtMoney(figee.etat === 'figee' ? figee.montant : r.caution_amount_eur)
    }
    case 'caution_link':           return r.caution_link || r.deposit_url || ''
    case 'paiement_sejour_status': return r.paiement_sejour_status || ''
    case 'paiement_sejour_link':   return r.paiement_sejour_link || ''
    case 'caution_authorized':     return r.caution_status === 'authorized' ? 'true' : ''
    case 'caution_captured':       return r.caution_status === 'captured' ? 'true' : ''
    case 'paid_at_formatted':      return r.paid_at ? fmtDate(r.paid_at, language) : ''
    // Sprint 2.4 PR-B1 (ADR-131) : URL caution Stripe auto. ctx.paiement_caution_url
    // injecté par lib/messages-engine.js (côté serveur uniquement). En UI admin,
    // valeur vide → preview affiche "{paiement_caution_url}" textuel.
    case 'paiement_caution_url':   return ctx.paiement_caution_url || ''

    // ── Chantier moteur méthode d'envoi (M5, ADR-267) — variables INVERSES ──
    // Non-vides quand la chose MANQUE → destinées aux blocs {#if …} négatifs
    // (« seulement si formulaire NON rempli » / « seulement si caution NON
    // reçue ») SANS toucher au parseur IF_BLOCK_RE. ⚠️ Conditionnelles-only :
    // utilisées inline hors {#if}, elles rendent "true" dans le message —
    // même contrat que caution_received/is_airbnb.
    // {caution_manquante} : dérivation source-aware (payment-derivation) —
    // Airbnb = AirCover → jamais manquante ; Booking/Direct → manquante tant
    // que caution_status ∉ CAUTION_RECUE_STATUSES (aligné _CAUTION_SATISFIED
    // livret ; inclut charged/refund_* depuis D2, GO Dimitri 2026-07-13).
    case "caution_manquante":
      return cautionRecue(r) ? "" : "true";
    // ── ADR-174 — MODE de caution (empreinte vs débit), reflet des textes ──
    // {caution_empreinte} / {caution_debit} : dérivés du nombre de nuits via la
    // SOURCE UNIQUE cautionModeForNights (src/lib/caution-mode.js, partagé avec le serveur) —
    // EXACTEMENT le seuil qui pilote capture_method côté Stripe (api/stripe.js).
    // Nuits calculées sur les MÊMES champs de date que {date_arrivee}/{date_depart}
    // (r.start_date/r.end_date). EXCLUSIVES & EXHAUSTIVES : exactement UNE des
    // deux vaut "true" quand la caution s'applique. Conditionnelles-only (comme
    // caution_manquante). FAIL-SAFE dates absentes/invalides → nbNuits 0 →
    // 'empreinte' (mode le plus prudent, conforme au fallback pessimiste Stripe).
    case "caution_empreinte":
      return _cautionSejour(ctx).mode === "empreinte"
        ? "true"
        : "";
    case "caution_debit":
      return _cautionSejour(ctx).mode === "debit" ? "true" : "";
    // ── D2/A-8 (LOT 2 — surface SMS) — variables EFFECTIVES matrice-aware ──
    // MÊME code que lib/template-variables.js (F10 phase 2, 03/10). ⚠️ Au
    // 03/10, NI l'envoi réel (lib/messages-engine.js) NI les aperçus ne posent
    // ctx.journey_composition_config : partout, la matrice est absente et le
    // résolveur applique les DÉFAUTS CODÉS (airbnb → non, booking/direct →
    // oui). L'aperçu dit donc exactement ce que dit l'envoi. Brancher la vraie
    // matrice = décision de Dimitri, à faire côté ENVOI d'abord, puis ici
    // (jamais l'aperçu seul : il contredirait l'envoi). ⚠️ Les gabarits SMS
    // doivent consommer la variable EFFECTIVE (jamais le littéral).
    case "caution_manquante_effective":
      return matrixCautionOn({
        source: r.source,
        logementId: r.logement_id,
        configRows: ctx.journey_composition_config,
      }) &&
        !_cautionLogementAZero(ctx) &&
        !cautionRecue(r)
        ? "true"
        : "";
    case "caution_required_effective":
      return (r.source === "booking" || r.source === "direct") &&
        matrixCautionOn({
          source: r.source,
          logementId: r.logement_id,
          configRows: ctx.journey_composition_config,
        }) &&
        !_cautionLogementAZero(ctx)
        ? "true"
        : "";
    // {formulaire_manquant} : source = ctx.formulaire_rempli (booléen injecté
    // par le moteur serveur). En preview admin, ctx absent/undefined →
    // considéré NON rempli → le bloc de rappel s'affiche.
    case "formulaire_manquant":
      return ctx.formulaire_rempli === true ? "" : "true";
    // Miroir positif (bloc « seulement si formulaire déjà rempli »).
    case "formulaire_rempli":
      return ctx.formulaire_rempli === true ? "true" : "";

    // ── LOT 3 — prédicat « dossier de réservation COMPLET » ──────────────
    // MIROIR EXACT du serveur (lib/template-variables.js) : lecture PURE du
    // ctx, jamais de dérivation ici. Le booléen est calculé par
    // lib/messages-engine.js (seul détenteur de la matrice caution + du signal
    // formulaire batché). Sert l'exclusivité SMS 1 / SMS 2.
    // Preview admin : ctx.reservation_complete absent → INCOMPLET (fail-safe,
    // même direction que formulaire_manquant) — jamais de crash.
    case "reservation_complete":
      return ctx.reservation_complete === true ? "true" : "";
    case "reservation_incomplete":
      return ctx.reservation_complete === true ? "" : "true";

    default:
      return null; // signal "variable inconnue" — préservée textuellement
  }
}

// ── Sections conditionnelles {#if var}…{/if} ───────────────────────
//
// Phase 2.A Étape 2 : un bloc {#if X}…{/if} est conservé seulement si
// resolveValue(X, ctx) retourne une chaîne non-vide après trim. Sinon
// le bloc entier (balises incluses) est supprimé.
//
// Multi-lignes supporté (regex non-greedy + [\s\S]). Pas de nesting.
// Une variable utilisée UNIQUEMENT dans un bloc inactif n'apparaîtra
// pas dans le rendu final → ni renderTemplate ni findMissingVars
// (côté serveur) ne la traitent comme manquante.
//
// LIGNE BLANCHE RÉSIDUELLE (fix) : quand un bloc {#if} SEUL SUR SA LIGNE
// est masqué, on consomme sa ligne entière (indentation + contenu + saut
// de ligne terminal) pour ne laisser AUCUN trou. Pour cela la regex capture
// aussi l'indentation de tête (g1), les espaces (g4) et le saut de ligne
// terminal (g5) autour du bloc :
//   - bloc GARDÉ → on reconstruit EXACTEMENT le texte d'origine (g1+contenu
//     +g4+g5) → zéro régression sur l'espacement des messages qui l'affichent.
//   - bloc MASQUÉ seul-sur-ligne (précédé d'un début de ligne ET suivi d'un
//     saut/fin) → "" : la ligne disparaît, pas de ligne vide.
//   - bloc MASQUÉ inline (au milieu d'une phrase) → on ne consomme aucun
//     saut, on préserve indentation/espaces/saut voulus (comportement inchangé).
// Note : un bloc masqué en toute FIN de template sans saut terminal laisse le
// "\n" qui le précède intact (pas une ligne blanche au milieu du corps, et
// identique à l'ancien comportement) — on ne touche jamais un saut en amont.
// ⚠️ DUPLICATION CONTRÔLÉE (ADR-021) : logique identique dans le twin CJS
// lib/template-variables.js — toute modif s'applique aux 2 fichiers.

// La capture de l'expression (g2) accepte lettres/chiffres/_/espaces : le
// single-var {#if X} capture "X" à l'identique (byte-identité #1046 préservée),
// et le cumul {#if A ET B} capture "A ET B" pour parsing par evalIfExpression.
const IF_BLOCK_RE =
  /([ \t]*)\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}([\s\S]*?)\{\/if\}([ \t]*)(\r?\n)?/g;

// ── Cumul de conditions ET / OU (décision Dimitri) ─────────────────
// L'expression d'un bloc {#if …} accepte, EN PLUS de la variable unique
// historique, un cumul HOMOGÈNE de variables :
//   {#if A ET B ET C} → gardé ssi TOUTES résolvent non-vide (conjonction).
//   {#if A OU B OU C} → gardé ssi AU MOINS UNE résolve non-vide (disjonction).
// Chaque « variable » est un nom \w+ résolu par resolveValue (inconnue → ""
// → comptée fausse). Règle de vérité IDENTIQUE au single-var : non-vide
// après trim.
//
// Séparateurs : jetons « ET » / « OU » en MAJUSCULES STRICTES, isolés par des
// espaces (espaces multiples tolérés). Casse stricte VOLONTAIRE : on ne veut
// jamais confondre un mot « et »/« ou » avec l'opérateur. Comme une variable
// est toujours UN SEUL \w+ sans espace, la tokenisation par espaces ne peut
// pas casser un nom de variable (ex. caution_manquante reste un seul jeton).
//
// UN SEUL type d'opérateur par bloc. Un MÉLANGE ET/OU ({#if A ET B OU C}) a
// une précédence AMBIGUË : on ne la devine pas → expression NON SUPPORTÉE →
// bloc MASQUÉ (fail-safe), jamais de crash. Idem toute expression malformée
// (jeton inattendu, opérateur en tête/fin, expression vide). Le single-var
// reste STRICTEMENT inchangé (byte-identité, cf. test snapshot prod).
// ⚠️ DUPLICATION CONTRÔLÉE (ADR-021) : logique identique dans le twin CJS.
// PARTIE B (ADR-272 — fondation moteur riche Direct) : résolveur de vérité
// INJECTABLE (param optionnel `resolver`, défaut = resolveValue). Réplique
// byte-parallèle du twin CJS. INUTILISÉ côté ESM (le moteur riche Direct est
// CJS server-only) — présent pour la parité stricte ADR-021. Le canal Plateformes
// (ESM comme CJS) n'en passe JAMAIS → défaut resolveValue → comportement
// STRICTEMENT inchangé (byte-identité prouvée par les tests existants).
function evalIfExpression(expr, ctx, resolver = resolveValue) {
  const isTruthy = (name) => {
    const v = resolver(name, ctx);
    return v != null && String(v).trim() !== "";
  };
  const tokens = String(expr).trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false; // {#if } vide → fail-safe
  if (tokens.length === 1) return isTruthy(tokens[0]); // single-var : INCHANGÉ
  if (tokens.length % 2 === 0) return false; // structure attendue var (OP var)+ (impaire)

  const vars = [];
  const ops = [];
  for (let i = 0; i < tokens.length; i++) {
    (i % 2 === 0 ? vars : ops).push(tokens[i]);
  }
  const allEt = ops.every((o) => o === "ET");
  const allOu = ops.every((o) => o === "OU");
  if (!allEt && !allOu) return false; // mélange ET/OU ou opérateur inconnu → fail-safe
  return allEt ? vars.every(isTruthy) : vars.some(isTruthy);
}

export function evaluateConditions(template, ctx = {}, resolver = resolveValue) {
  if (!template) return "";
  return template.replace(
    IF_BLOCK_RE,
    (match, indent, expr, content, wsAfter, nlAfter, offset, full) => {
      const kept = evalIfExpression(expr, ctx, resolver);
      const trailingNl = nlAfter || "";

      if (kept) {
        // Reconstruction byte-identique au texte source (aucune régression).
        return indent + content + wsAfter + trailingNl;
      }

      // Masqué : bloc « seul sur sa ligne » ? (début de ligne ∧ fin de ligne)
      const atLineStart = offset === 0 || full[offset - 1] === "\n";
      const atLineEnd = trailingNl !== "" || offset + match.length === full.length;

      // Ligne entière consommée (saut terminal inclus) → pas de ligne blanche.
      if (atLineStart && atLineEnd) return "";

      // Inline : on retire le bloc mais on préserve les espaces/saut autour.
      return indent + wsAfter + trailingNl;
    },
  );
}

// ── Render principal ───────────────────────────────────────────────

// Rendu récursif (PR #142A — modèle Option B "variables injectables").
// Quand une variable comme {access_apartment_instructions} contient elle-
// même {box_code}, on doit refaire une passe pour résoudre les variables
// imbriquées injectées par la passe précédente.
//
// MAX_PASSES = 3 : suffisant pour 1-2 niveaux d'imbrication usuels (texte
// libre admin contient {box_code} → 1 niveau). Au-delà, on s'arrête sans
// throw : les {var} restantes restent textuelles (boucle stable). Cycle
// volontaire (var qui se réfère à elle-même) bloqué par MAX_PASSES.
//
// Stabilité : on break dès qu'une passe ne change rien (cas usuel avec
// 0 imbrication → 1 seule passe effective, pas de coût caché).
const RENDER_MAX_PASSES = 3;
const VAR_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

// Sentinels pour mode wrapHTML (PR #145). Caractères Unicode "Private
// Use Area" non collisionnables avec contenu utilisateur normal.
const PH_OPEN = "";
const PH_CLOSE = "";

function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c],
  );
}

// renderTemplate(body, ctx, options)
//   options.wrapHTML :
//     - undefined / false : rendu texte normal (prod), récursif
//     - 'unresolved' (UI default) : rendu récursif + wrap des {var}
//       restantes en <span class="var-unresolved">. Sortie HTML escapée.
//     - 'all' (UI toggle "surligner résolues") : single-pass wrap qui
//       wrap chaque substitution (résolue OU non). Compromis : les
//       variables imbriquées dans access_*_instructions apparaissent
//       comme contenu d'un bloc parent résolu (pas indépendamment).
//       Sortie HTML escapée.
export function renderTemplate(body, ctx = {}, options = {}) {
  if (!body) return "";
  const wrap = options.wrapHTML;
  let current = evaluateConditions(body, ctx);

  if (!wrap) {
    for (let i = 0; i < RENDER_MAX_PASSES; i++) {
      const next = current.replace(VAR_RE, (match, key) => {
        const v = resolveValue(key, ctx);
        return v == null ? match : String(v);
      });
      if (next === current) break;
      current = next;
    }
    return current;
  }

  // Mode wrap : on tag chaque substitution avec un sentinel + index
  // FRONT 2 — une variable CONNUE mais VIDE ('') est un « trou » à combler :
  // on la traite comme unresolved (wrappée + cliquable dans l'aperçu), pas comme
  // résolue silencieuse. `isHole` = null/undefined (inconnue) OU '' (vide connue).
  const isHole = (v) => v == null || v === "";
  const slots = [];
  if (wrap === "all") {
    current = current.replace(VAR_RE, (m, k) => {
      const v = resolveValue(k, ctx);
      if (isHole(v)) {
        slots.push({ kind: "unresolved", name: k });
      } else {
        slots.push({ kind: "resolved", name: k, value: String(v) });
      }
      return PH_OPEN + (slots.length - 1) + PH_CLOSE;
    });
  } else {
    // wrap === 'unresolved' (ou autre valeur truthy) : substitue uniquement les
    // variables résolues NON VIDES ; wrappe les trous (vides connues + inconnues).
    for (let i = 0; i < RENDER_MAX_PASSES; i++) {
      const next = current.replace(VAR_RE, (m, k) => {
        const v = resolveValue(k, ctx);
        return isHole(v) ? m : String(v);
      });
      if (next === current) break;
      current = next;
    }
    current = current.replace(VAR_RE, (m, k) => {
      slots.push({ kind: "unresolved", name: k });
      return PH_OPEN + (slots.length - 1) + PH_CLOSE;
    });
  }

  const parts = current.split(new RegExp(`${PH_OPEN}(\\d+)${PH_CLOSE}`));
  let html = "";
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) {
      html += escapeHtml(parts[i]);
    } else {
      const slot = slots[Number(parts[i])];
      if (slot.kind === "resolved") {
        const trunc =
          slot.value.length > 60 ? slot.value.slice(0, 60) + "…" : slot.value;
        const title = `Variable {${slot.name}} = ${trunc}`;
        html += `<span class="var-resolved" data-var="${escapeHtml(slot.name)}" title="${escapeHtml(title)}">${escapeHtml(slot.value)}</span>`;
      } else {
        // FRONT 2 — `data-var` ajouté pour rendre les variables VIDES/inconnues
        // cliquables dans l'aperçu du Livret (navigation vers la carte source).
        // Symétrie ADR-021 : le twin CJS (lib/template-variables.js) n'a PAS de
        // branche wrapHTML (rendu serveur plain only) — rien à dupliquer là-bas.
        html += `<span class="var-unresolved" data-var="${escapeHtml(slot.name)}" title="Variable inconnue ou vide pour ce logement — vérifier le formulaire admin">{${escapeHtml(slot.name)}}</span>`;
      }
    }
  }
  return html;
}

// Pour les tooltips du panneau d'insertion : renvoie { value, label }
// où label est tiré du catalogue listAvailableVariables.
export function resolveAndDescribe(key, ctx) {
  const value = resolveValue(key, ctx);
  let label = null;
  for (const cat of listAvailableVariables()) {
    const found = cat.variables.find((x) => x.key === key);
    if (found) {
      label = found.label;
      break;
    }
  }
  return { value, label };
}
